import moment from "moment";

import { NullableNumber } from "types/Reports";

// the individual bar display is:
// - The maximum value is 100% of the max width
// - The minimum value is 0% of the max width with a minimum visible bar
export const computeHorizontalBarLength = (
    maxValue: NullableNumber,
    minValue: NullableNumber,
    value: NullableNumber,
    maxWidth: number,
) => {
    if (
        value === null || maxValue === null || minValue === null ||
        (value === 0 && maxValue === 0 && minValue === 0)
    ) {
        return 0;
    }

    let width = 0;
    // this is to guarantee that the ratio is always there even the number is extremely small
    const minBar = 5;

    if (maxValue >= 0 && minValue >= 0) {
        const ratio = (maxWidth - minBar) / maxValue;
        width = minBar + ratio * value;
    } else if (maxValue < 0 && minValue < 0) {
        const absValue = Math.abs(maxValue + minValue);
        const ratio = (maxWidth- minBar) / absValue;
        width = minBar + ratio * (absValue - Math.abs(value));
    } else {
        const ratio = (maxWidth - minBar) / (Math.abs(maxValue) + Math.abs(minValue));

        if (value >= 0) {
            width = minBar + (value + Math.abs(minValue)) * ratio;
        } else {
            width = minBar + (Math.abs(minValue) - Math.abs(value)) * ratio;
        }
    }

    return width;
};

export const computeDayInMonthByIndex = (
    targetDayIndex: number,
    lastDayOfMonthIndex: number,
    lastDayOfMonth: moment.Moment,
) => {
    const difIndex = lastDayOfMonthIndex - targetDayIndex;
    const targetDay = moment.unix(lastDayOfMonth.unix()).subtract(difIndex, 'd');

    return targetDay;
};

export const computeIndexByDayInMonth = (categories: string[], date: number) => {
    for (let i = 0; i < categories.length; i++) {
        if (categories[i] === `${date}`) {
            return i;
        }
    }

    return null;
};

/**
 * Compute fractional hours from a moment to the end of its day (start of next day).
 */
export const hoursUntilEndOfDay = (m: moment.Moment): number => {
    return moment(m).add(1, 'day').startOf('day').diff(m, 'hours', true);
};

/**
 * Compute fractional hours from the start of a moment's day to that moment.
 */
export const hoursSinceStartOfDay = (m: moment.Moment): number => {
    return m.diff(moment(m).startOf('day'), 'hours', true);
};

/**
 * Resolve the effective end date for an ongoing period (endTime === null).
 * - Same day as start → now
 * - Same month as start → now
 * - Otherwise → end of start's month
 */
export const resolveOngoingEnd = (
    startDate: moment.Moment,
    categoryMonth: moment.Moment,
): moment.Moment => {
    const now = moment();

    if (now.isSame(startDate, 'month')) {
        return now;
    }

    return moment(categoryMonth).endOf('month');
};

/**
 * Fill the result array for a contiguous range [startIdx, endIdx].
 * - startIdx gets startHours
 * - middle indices get 24
 * - endIdx gets endHours (only if different from startIdx)
 */
export const fillRange = (
    result: NullableNumber[],
    startIdx: number,
    startHours: number,
    endIdx: number,
    endHours: number,
): void => {
    const HOURS_PER_DAY = 24;
    result[startIdx] = (result[startIdx] ?? 0) + startHours;

    for (let i = startIdx + 1; i < endIdx; i++) {
        result[i] = HOURS_PER_DAY;
    }

    if (endIdx > startIdx) {
        result[endIdx] = (result[endIdx] ?? 0) + endHours;
    }
};

/**
 * Accumulate hours into the result array for a given ON period.
 * There are several cases
 * - Start time and end time are in the same month
 *   - Start time and end time are NOT in the same day: both days need to get a corresponding period of time of them
 *   - Start time and end time are in the same day: this day gets a period of time
 * - Start time and end time are NOT in the same month: category day get the corresponding period of time.
 *   - Start time is in the category month & end time is NOT (or = null).
 *   - End time is in the category month and start time is NOT (or = null).
 * @param result an array which stores numbers of hours for the category days
 * @param categories an array which stores dates of a month
 * @param startTime the start time unix
 * @param endTime the end time unix
 */
export const accumulateHours = (
    result: NullableNumber[],
    categories: string[],
    startTime: number,
    endTime: number | null,
    categoryMonth: moment.Moment,
): void => {
    const startDate = moment.unix(startTime);
    const startInMonth = categoryMonth.isSame(startDate, 'month');

    const endDate = endTime === null
        ? resolveOngoingEnd(startDate, categoryMonth)
        : moment.unix(endTime);

    const endInMonth = categoryMonth.isSame(endDate, 'month');

    // Neither boundary falls in the category month — nothing to accumulate
    if (!startInMonth && !endInMonth) {
        return;
    }

    // Clamp boundaries to the category month
    const clampedStart = startInMonth ? startDate : moment(categoryMonth).startOf('month');
    const clampedEnd = endInMonth ? endDate : moment(categoryMonth).endOf('month');

    const startIdx = computeIndexByDayInMonth(categories, clampedStart.date());
    const endIdx = computeIndexByDayInMonth(categories, clampedEnd.date());

    if (startIdx === null || endIdx === null) {
        return;
    }

    const startHours = startIdx === endIdx
        ? clampedEnd.diff(clampedStart, 'hours', true)
        : hoursUntilEndOfDay(clampedStart);

    const endHours = hoursSinceStartOfDay(clampedEnd);

    fillRange(result, startIdx, startHours, endIdx, endHours);
};

/**
 * Convert the room status or room occupancy from many continuous 0 (Offline) or 1 (Online) at a time to
 * a number of hours in a day
 * The deviceRawData is started from the last day of the previous month until the last day of the input month
 * @param deviceRawData is an array of an array with 2 values: timestamp in unix number and the status 0 or 1
 * @param categories the day in the month
 * @param endDayOfMonth: the last day in the month
 */
export const diffValueAndGroupRawDataByDay = (
    deviceRawData: [number, string][],
    categories: string[],
    endDayOfMonth: moment.Moment,
) => {
    const result: NullableNumber[] = categories.map(() => null);

    if (deviceRawData.length === 0) {
        return result;
    }

    const STATUS_ON = '1';

    // aggregate and compute all periods except the last one
    let prevStatus = deviceRawData[0][1];
    let prevTime = deviceRawData[0][0];

    for (let i = 1; i < deviceRawData.length; i++) {
        const [currentTime, currentStatus] = deviceRawData[i];
        const day = moment.unix(currentTime);

        // calculate all on time if the previous status is ON
        if (prevStatus === STATUS_ON && day.isSame(endDayOfMonth, 'month')) {
            accumulateHours(result, categories, prevTime, currentTime, endDayOfMonth);
        }

        prevStatus = currentStatus;
        prevTime = currentTime;
    }

    // last period
    if (prevStatus === STATUS_ON) {
        accumulateHours(result, categories, prevTime, null, endDayOfMonth);
    }

    return result;
};

/**
 * Get the average value of sensors raw data
 * @param deviceRawData is an array of an array with 2 values: timestamp in unix number and the status 0 or 1
 * @param categories the day in the month
 */
export const avgValueAndGroupRawDataByDay = (
    deviceRawData: [number, string][],
    categories: string[],
) => {
    const sum: NullableNumber[] = categories.map(() => null);

    if (deviceRawData.length === 0) {
        return sum;
    }

    const count = categories.map(() => 0);

    for (const interval of deviceRawData) {
        const time = interval[0];
        const value = Number.parseFloat(interval[1]);
        const date = moment.unix(time).date();

        const idx = computeIndexByDayInMonth(categories, date);

        if (idx === null) {
            continue;
        }

        sum[idx] = (sum[idx] ?? 0) + value;
        count[idx] += 1;
    }

    return sum.map((total, i) =>
        total !== null && count[i] > 0 ? total / count[i] : null
    );
};

/**
 * Get the sum of pulses value of water leakage detection sensors raw data
 * @param deviceRawData is an array of an array with 2 values: timestamp in unix number and the status 0 or 1
 * @param categories the day in the month
 * @param endDayOfMonth the last day in the month
 */
export const sumPulseAndGroupRawDataByDay = (
    deviceRawData: [number, string][],
    categories: string[],
    endDayOfMonth: moment.Moment,
) => {
    const result: NullableNumber[] = categories.map(() => null);

    if (deviceRawData.length === 0) {
        return result;
    }

    const isRisingEdge = (current: number, previous: number): boolean =>
        previous === 0 && current !== 0;

    for (let i = 1; i < deviceRawData.length; i++) {
        const [time, rawValue] = deviceRawData[i];
        const [, prevRawValue] = deviceRawData[i - 1];

        const value = Number.parseInt(rawValue);
        const prevValue = Number.parseInt(prevRawValue);
        const day = moment.unix(time);

        if (!day.isSame(endDayOfMonth, 'month') || !isRisingEdge(value, prevValue)) {
            continue;
        }

        const date = day.date();
        const idx = computeIndexByDayInMonth(categories, date);

        if (idx !== null) {
            result[idx] = (result[idx] ?? 0) + 1;
        }
    }

    return result;
};
